import { create } from 'zustand'
import type {
  AuthSessionModel,
  AuthStatus,
  AuthUserModel,
} from '../models/auth.model'
import { authService, type SignupPayload } from '../services/auth.service'
import { sessionStorageRepo } from '../services/session.storage'
import { clearGuestSession } from '../services/guest-session'
import { clearIndexedDb } from '../services/local-data'
import { toAuthError } from '../services/auth-error'
import { scheduleSessionRefresh, stopSessionRefresh } from '@/lib/auth-refresh'
import { clearSdkSessionToken } from '@/lib/sdk-session-token'
import { AUTH_ROUTES } from '@/shared/constants/routes.constants'

/** The `state` of a SecureID logout this tab started; the callback page reads it. */
export const SECUREID_LOGOUT_STATE_KEY = 'secureid_logout_state'

type AuthState = {
  user: AuthUserModel | null
  session: AuthSessionModel | null
  status: AuthStatus
  error: string | null
  rememberMe: boolean
  /**
   * Per-org chat/presence identity (`contactPresenceId`, from the SDK server's
   * `contacts.lookupSelf` / `registerSelf`). CHAT and PRESENCE key on THIS id
   * ("is me" comparisons, optimistic senderId, presence topic ids) — NOT on
   * `user.id`. Null until resolved at login (or the user consents via the
   * terms gate); while null, chat/presence subscriptions simply don't act.
   * `user.id` (the SecureID user id) stays the identity for meetings/profile.
   */
  presenceId: string | null
}

type AuthActions = {
  hydrate: () => void
  // No `login(credentials)`. A session comes from SecureID OIDC only —
  // LoginPage redirects to the authorize URL and OidcCallbackPage calls
  // `setSession` with what the backend issued.
  signup: (payload: SignupPayload) => Promise<void>
  /**
   * `endSecureIdSession` — the user pressed Log out: also end their SecureID
   * session, so the next sign-in asks for an email and password.
   */
  logout: (opts?: { endSecureIdSession?: boolean }) => Promise<void>
  setSession: (
    session: AuthSessionModel,
    user: AuthUserModel,
    rememberMe?: boolean,
  ) => void
  /**
   * Apply a silently-renewed session.
   *
   * Distinct from `setSession`, which is a LOGIN: that one clears the guest
   * marker, rewrites storage and resets `presenceId` to null. Doing any of that
   * on a routine renewal would drop the resolved tenant identity every fifteen
   * minutes and take presence and chat subscriptions down with it.
   *
   * Storage is not written here — `auth-refresh` has already persisted the blob
   * every feature service reads. This exists purely so the IN-MEMORY copy the
   * route guard reads agrees with it.
   */
  applyRefreshedSession: (session: AuthSessionModel, user?: Partial<AuthUserModel>) => void
  /** Set (or clear) the resolved tenant presence identity. */
  setPresenceId: (presenceId: string | null) => void
  /**
   * Update the signed-in user's picture after an upload or removal.
   *
   * Every avatar on screen reads `user.avatarUrl` from this store, so without
   * this the header would keep showing the old picture until a full reload —
   * the change would look like it hadn't taken.
   */
  setAvatarUrl: (avatarUrl: string | null) => void
  /**
   * Merge server-side user fields into the session, in memory AND in storage.
   *
   * Persisting is the point: updates that only touched the in-memory user were
   * undone by the next `hydrate()`, which restores the blob written at login.
   * That is how an uploaded picture could show in the header immediately and be
   * gone after a reload.
   */
  mergeUser: (patch: Partial<AuthUserModel>) => void
  clear: () => void
}

const INITIAL_STATE: AuthState = {
  user: null,
  session: null,
  status: 'idle',
  error: null,
  rememberMe: false,
  presenceId: null,
}

export const useAuthStore = create<AuthState & AuthActions>((set, get) => ({
  ...INITIAL_STATE,

  hydrate: () => {
    const persisted = sessionStorageRepo.load()
    if (persisted) {
      set({
        session: persisted.session,
        user: persisted.user,
        status: 'authenticated',
        error: null,
        // Restore the chat/presence identity so subscriptions start on the FIRST
        // render after a reload. Without this every reload left presenceId null
        // until `GET /contacts/self` came back — and during that window the owner
        // channel never joined (so you looked offline to everyone) and no live
        // message deltas arrived. `useContactRegistration` still runs and
        // re-publishes the authoritative value.
        presenceId: persisted.presenceId ?? null,
      })
      // Arm silent renewal. The access token is short-lived now, so without
      // this a reloaded tab would work only until the token it restored aged
      // out — typically well under 15 minutes.
      scheduleSessionRefresh()
    } else {
      set({ status: 'idle' })
    }
  },

  signup: async (payload) => {
    set({ status: 'authenticating', error: null })
    try {
      // Signup does not auto-login: SecureID returns the created user and
      // tenant but no tokens, so there is no session to set. The page sends the
      // user to /login. We still surface any validation failure here.
      await authService.signup(payload)
      set({ status: 'idle', error: null })
    } catch (err) {
      const authErr = toAuthError(err)
      set({ status: 'error', error: authErr.message })
      throw authErr
    }
  },

  logout: async (opts) => {
    // Stop renewal FIRST. A timer that fires mid-sign-out would re-mint a token
    // for the session we are in the middle of revoking, and write it straight
    // back into the storage we just cleared.
    stopSessionRefresh()
    // The minted SDK token outlives the app session (1h vs 15m) and was never
    // being dropped, so it stayed in memory after sign-out.
    clearSdkSessionToken()
    let secureIdLogout: { logoutUrl: string; state: string } | null = null
    try {
      secureIdLogout = await authService.logout(
        opts?.endSecureIdSession ? `${window.location.origin}${AUTH_ROUTES.OIDC_CALLBACK}` : undefined,
      )
    } finally {
      sessionStorageRepo.clear()
      set({ ...INITIAL_STATE })
    }
    // Cached chats and recording chunks stay behind otherwise, for the next
    // person at this browser (services/local-data). Before the redirect below,
    // which would cut the deletes short.
    await clearIndexedDb()
    // Our session is gone, but SecureID's is not: "Sign in" came straight back
    // signed in, no email or password asked. Its logout page ends it (the user
    // confirms there) and returns to the callback, which goes on to sign-in.
    if (secureIdLogout) {
      try { sessionStorage.setItem(SECUREID_LOGOUT_STATE_KEY, secureIdLogout.state) } catch { /* storage blocked */ }
      window.location.assign(secureIdLogout.logoutUrl)
    }
  },

  setSession: (session, user, rememberMe = get().rememberMe) => {
    // A real login takes over this tab — drop any guest marker so presence and
    // other authenticated-only features resume here.
    clearGuestSession()
    sessionStorageRepo.save({ session, user }, rememberMe)
    set({ session, user, rememberMe, status: 'authenticated', error: null, presenceId: null })
    scheduleSessionRefresh()
  },

  applyRefreshedSession: (session, user) => {
    const current = get().user
    set({
      session,
      status: 'authenticated',
      error: null,
      ...(user && current ? { user: { ...current, ...user } } : {}),
    })
  },

  setPresenceId: (presenceId) => {
    const userId = get().user?.id
    if (userId) sessionStorageRepo.savePresenceId(userId, presenceId)
    set({ presenceId })
  },

  setAvatarUrl: (avatarUrl) => {
    get().mergeUser({ avatarUrl: avatarUrl ?? undefined })
  },

  mergeUser: (patch) => {
    const user = get().user
    if (!user) return
    const next = { ...user, ...patch }
    set({ user: next })
    sessionStorageRepo.patchUser(user.id, patch)
  },

  clear: () => {
    stopSessionRefresh()
    clearSdkSessionToken()
    sessionStorageRepo.clear()
    set({ ...INITIAL_STATE })
  },
}))

// Selectors — stable references for subscribing components.
export const selectIsAuthenticated = (s: AuthState & AuthActions) =>
  s.status === 'authenticated' && !!s.session && s.session.expiresAt > Date.now()
